import { Key } from "@solid-primitives/keyed"
import { useWindowSize } from "@solid-primitives/resize-observer"
import { Component, createEffect, createMemo, createSignal, For, useContext, type Accessor, type JSX } from "solid-js"
import { Engine } from "../../lib/core/engine/Engine.type"
import type { PaintTileOption } from "../../lib/core/map_editor/paint_tile_option"
import { PaintTileOptionFolder, paintTileOptionFolders } from "../../lib/core/map_editor/paint_tile_option_folder"
import { paintTileOptionList } from "../../lib/core/map_editor/paint_tile_option_list"
import { deepClone } from "../../lib/deep_clone"
import closeDropDownWhenOtherDropDownOpens from "../../lib/event/closeDropDownWhenOtherDropDownOpens"
import { isPlainObjDeeplyEqual } from "../../lib/isPlainObjDeeplyEqual"
import type { Nullable } from "../../typescript"
import EngineContext from "../EngineContext"
import SelectPlayerInput from "./SelectPlayerInput"
import SingleEntityIcon3D from "./SingleEntityIcon3D"

type PaintTileOptionOrNull = Nullable<PaintTileOption>

const PaintTileSelect: Component = () => {
  const engine: Engine = useContext(EngineContext)

  const [isDropDownOpen, setIsDropDownOpen] = createSignal<boolean>(false)

  function toIcon(paintTileOption: PaintTileOption, style: JSX.CSSProperties | undefined) {
    if (paintTileOption.entity) {
      const getEntity = createMemo(() => {
        const entity = deepClone(paintTileOption.entity)

        const player_id = engine.selectedPlayerId
        if (entity && ('player_id' in entity)) {
          entity.player_id = player_id
        }

        return entity
      })

      return <SingleEntityIcon3D entity={getEntity()} size={40} style={style} />
    }
  }

  const windowSize = useWindowSize()

  const isSelected = (paintTileOption: PaintTileOptionOrNull) => {
    return isPlainObjDeeplyEqual(paintTileOption, engine.paintToolOption)
  }

  return <div id="MapEditorPaintTileSelect" class="card-body w-20-rem">
    {/* <h5 class="card-title">PaintTileSelect</h5> */}
    <SelectPlayerInput />
    <div class="dropdown">
      <button class="btn btn-secondary dropdown-toggle w-100" type="button" aria-expanded="false"
        onClick={() => { setIsDropDownOpen(!isDropDownOpen()) }}
      >
        {!engine.paintToolOption && 'Select Tile'}
        <For each={paintTileOptionList} children={(paintTileOption: PaintTileOption) => {
          return <>{toIcon(paintTileOption, {
            display: isSelected(paintTileOption) ? 'inline' : 'none',
            'margin': '0 0.25rem',
          })}</>
        }
        } /> {engine.paintToolOption && (engine.paintToolOption.dname || 'unknown')}
      </button>
      <ul class="dropdown-menu" classList={{ 'dropdown-menu': true, show: isDropDownOpen(), 'w-100': true }}
        ref={(ref) => {
          createEffect(() => {
            // read some signals for triggering
            isDropDownOpen()
            const windowHeight = windowSize.height
            if (ref) {
              const bcr = ref.getBoundingClientRect()
              const maxHeightStr = `${parseInt(((windowHeight * 0.9) - bcr.top) as unknown as string)}px`
              ref.style.setProperty('max-height', maxHeightStr)
              ref.style.setProperty('overflow-y', 'scroll')
            }
          })

          closeDropDownWhenOtherDropDownOpens(ref, isDropDownOpen, setIsDropDownOpen)
        }}>
        <Key each={paintTileOptionFolders} by="id">
          {(getFolder: Accessor<PaintTileOptionFolder>, index: Accessor<number>) => {
            const optionsInFolder = createMemo(() => {
              const folder = getFolder()
              return paintTileOptionList.filter(s => s.folder_id === folder.id)
              //.sort(byIdAsc)
            })
            return <>
              {index() > 0 && <hr />}
              <For each={optionsInFolder()}>
                {(paintTileOption: PaintTileOption) => {
                  return <>
                    <li>
                      <button class="dropdown-item" type="button"
                        onClick={() => {
                          engine.paintToolOption = paintTileOption
                          setIsDropDownOpen(false)
                        }}
                      >
                        {toIcon(paintTileOption, {
                          'margin': '0 0.25rem',
                        })} {paintTileOption.dname}
                      </button>
                    </li>
                  </>
                }}
              </For>
            </>
          }}
        </Key>
      </ul>
    </div>
  </div>
}

export default PaintTileSelect
